import { uniq } from "lodash";
import React, { Dispatch, memo, useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import PriceZone from "../../../../services/PriceZone";
import { useTypedSelector } from "../../../../redux/store";
import { createObjectLanguageNames } from "../../../../assets/languages/langs";
import { useTaxiServiceFilterAccessControl } from "../../../../hooks";
import MultiSelectWithModalBase, {
	MultiSelectWithModal,
} from "../MultiSelectWithModal";

const MultiSelectPriceZoneWithModalBase: React.FC<
	MultiSelectPriceZoneWithModalBase.Props
> = ({
	value,
	onChange,
	disabled,
	titleText,
	required,
	modalSetting,
	addOutsidePriceZone,
	outsidePriceZones,
	setOutsidePriceZones,
	sort,
	taxiServiceIds,
	...props
}) => {
	const { t } = useTranslation();
	const { accessTaxiServicesIds } = useTaxiServiceFilterAccessControl<any>();
	const lang = useTypedSelector((state) => state.session.language);
	const items = useTypedSelector(
		(state) => state.globalState.priceZones.data.subscription.basic,
	);

	const [error, setError] = useState<string | boolean>("");

	const texts = useMemo(
		() => ({
			outsidePriceZonesName:
				t([
					`pages.settings.pages.tariffs.tabs.auto.modal.content.tabs.main.rules.content.ruleOutput.tariffZonesOutput.str200`,
				]) || "",
			title: t(titleText),
			allText: t(["all_prise_zone"]),
			allActiveText: t(["all_active_prise_zone"]),
		}),
		[t, titleText],
	);

	const addOutsidePriceZoneId = useMemo(() => -111, []);

	const outsidePriceZone = useMemo(() => {
		const payload: PriceZone.Model = {
			name: createObjectLanguageNames(texts.outsidePriceZonesName),
			id: addOutsidePriceZoneId,
			taxiServiceId: 0,
			position: 0,
			vertices: [],
			active: false,
			bySector: false,
			createdAt: "",
			updatedAt: "",
			deletedAt: null,
		};

		return payload;
	}, [addOutsidePriceZoneId, texts.outsidePriceZonesName]);

	const _items = useMemo(() => {
		const filterTsIds = taxiServiceIds || accessTaxiServicesIds;
		const data = items
			? items?.filter((item) => filterTsIds.includes(item.taxiServiceId))
			: [];

		if (addOutsidePriceZone) {
			return [outsidePriceZone, ...data];
		}

		return data;
	}, [
		accessTaxiServicesIds,
		addOutsidePriceZone,
		items,
		outsidePriceZone,
		taxiServiceIds,
	]);

	const selectOptions = useMemo<MultiSelectWithModalBase.Value>(() => {
		const tariffZones = _items.map((item) => ({
			key: item.id,
			label: item.name?.[lang],
			value: item.id,
			name: item.name?.[lang],
			data: item,
		}));

		return tariffZones;
	}, [_items, lang]);

	const selected = useMemo<MultiSelectWithModalBase.Value>(() => {
		if (addOutsidePriceZone && outsidePriceZones) {
			const addIds = uniq([addOutsidePriceZoneId, ...value]);
			return selectOptions.filter((item) =>
				addIds.find((data) => data === item.value),
			);
		}

		return selectOptions.filter((item) =>
			value.find((data) => data === item.value),
		);
	}, [
		addOutsidePriceZone,
		addOutsidePriceZoneId,
		outsidePriceZones,
		selectOptions,
		value,
	]);

	const selectOnChange = useCallback(
		(newValue) => {
			setError("");
			const ids: number[] = newValue.map((item) => item.value);

			if (!addOutsidePriceZone) {
				onChange(ids);
				return;
			}

			const exist = ids.find((id) => id === addOutsidePriceZoneId);
			if (exist) setOutsidePriceZones?.(true);
			else setOutsidePriceZones?.(false);

			const newIds = ids.filter((id) => id !== addOutsidePriceZoneId);
			onChange(newIds);
		},
		[
			addOutsidePriceZone,
			addOutsidePriceZoneId,
			onChange,
			setOutsidePriceZones,
		],
	);

	const lengthValue = useMemo(() => {
		const existAddOptions = selectOptions?.find?.(
			(item) => item.value === addOutsidePriceZoneId,
		);
		const existAddValue = selected?.find?.(
			(item) => item.value === addOutsidePriceZoneId,
		);

		const optionsLength =
			existAddOptions && !existAddValue
				? (selectOptions?.length || 1) - 1
				: selectOptions?.length;

		return {
			optionsLength,
			valueLength: selected.length,
		};
	}, [addOutsidePriceZoneId, selectOptions, selected]);

	const allText = useMemo(() => {
		if (addOutsidePriceZone && outsidePriceZones) {
			return outsidePriceZones
				? `${texts.allText} + ${texts.outsidePriceZonesName}`
				: texts.allText;
		}
		return texts.allText;
	}, [
		addOutsidePriceZone,
		outsidePriceZones,
		texts.allText,
		texts.outsidePriceZonesName,
	]);

	return (
		<MultiSelectWithModal
			value={selected}
			onChange={selectOnChange}
			options={selectOptions}
			error={error}
			disabled={disabled}
			title={texts.title}
			allText={allText}
			allActiveText={texts.allActiveText}
			required={required}
			pullUpItemInsideArray
			hiddenButton={false}
			showSelectAll
			modalSetting={modalSetting}
			optionsLength={lengthValue.optionsLength}
			valueLength={lengthValue.valueLength}
			sort={{
				show: true,
				active: true,
				...(sort || {}),
			}}
			{...props}
		/>
	);
};

export const MultiSelectPriceZoneWithModal = memo(
	MultiSelectPriceZoneWithModalBase,
);

declare namespace MultiSelectPriceZoneWithModalBase {
	interface Props extends MultiSelectWithModalBase.Setting {
		value: Value;
		onChange: Dispatch<Value>;
		titleText: string | string[];
		outsidePriceZones?: boolean;
		setOutsidePriceZones?: React.Dispatch<boolean>;
		addOutsidePriceZone?: boolean;
		modalSetting?: MultiSelectWithModalBase.ListSelectProps["modalSetting"];
		taxiServiceIds?: number[];
		showTaxiServices?: boolean;
		showCompanies?: boolean;
	}

	type Value = number[];
}

export default MultiSelectPriceZoneWithModalBase;
